import { useEffect, useState } from "react";
import { Button, Card } from "@bht/ui";
import { getFeedbackReplies, markFeedbackReplySeen, type FeedbackReplyRow } from "../lib/api.js";

/**
 * Shows a reply to feedback the learner sent us.
 *
 * WHY. Someone asked us a real question through the feedback bubble and there
 * was no path for an answer to reach them: no email provider in the stack, and
 * nothing in the app that could carry a message back. So feedback was a one-way
 * channel, which is a bad thing to have if you are asking people to report
 * problems in your questions.
 *
 * Deliberately quiet: it renders nothing unless there is an unseen reply, shows
 * what they wrote so the answer has context, and dismisses permanently.
 */

export function FeedbackReply() {
  const [replies, setReplies] = useState<FeedbackReplyRow[]>([]);

  useEffect(() => {
    let live = true;
    getFeedbackReplies()
      .then((r) => {
        if (live) setReplies(r.replies ?? []);
      })
      // Never let this break the page it sits on; it is an extra, not a feature
      // anyone is waiting for.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const r = replies[0];
  if (!r) return null;

  function dismiss(id: string) {
    setReplies((prev) => prev.filter((x) => x.id !== id));
    void markFeedbackReplySeen(id).catch(() => {});
  }

  return (
    <Card>
      <div style={{ display: "grid", gap: 10 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "var(--accent-amber)",
          }}
        >
          About what you reported
        </div>
        {/* Only worth quoting back when they actually wrote something. A flag
            has no message, so the quote block would just repeat the heading. */}
        {r.message && r.message.length > 40 && (
          <blockquote
            style={{
              margin: 0,
              paddingLeft: 12,
              borderLeft: "2px solid var(--border-line)",
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              color: "var(--fg-2)",
            }}
          >
            {r.message}
          </blockquote>
        )}
        <p
          style={{
            margin: 0,
            fontFamily: "var(--font-sans)",
            fontSize: 15,
            lineHeight: 1.55,
            color: "var(--fg-1)",
            // A reply can now carry SEVERAL explanations, one per flag we
            // resolved, separated by a blank line. HTML collapses those
            // newlines, so without this two reasons render as one run-on
            // paragraph. Added 2026-10-29 with the per-flag reasons.
            whiteSpace: "pre-wrap",
          }}
        >
          {r.reply}
        </p>
        <div>
          <Button variant="ghost" onClick={() => dismiss(r.id)}>
            Got it
          </Button>
        </div>
      </div>
    </Card>
  );
}
